import { z } from 'zod';

import { OrganizationRole } from './organization';
import { PERMISSION } from '../../access-control/permission';
import { EmailStringSchema } from '../../common';
import { makeEndpoint, paginateQuerySchema, paginateResponseSchema } from '../common';
import { BrandMembershipRolesSchema } from '../user/user';

export enum OrganizationUserStatus {
  ACTIVE = 'active',
  INVITED = 'invited',
}

export enum OrganizationUserFilterQuery {
  ORGANIZATION_ADMIN = 'orgAdmin',
  ALL = 'all',
}

export const OrganizationUserStatusesSchema = z.nativeEnum(OrganizationUserStatus);

export const OrganizationUserRolesSchema = z.nativeEnum(OrganizationRole);

export const OrganizationUserSchema = z.object({
  createdAt: z.string().datetime(),
  profilePicture: z
    .object({
      id: z.string(),
      uri: z.object({ original: z.string() }).and(z.record(z.string())),
    })
    .nullable(),
  firstName: z.string(),
  lastName: z.string(),
  emailAddress: EmailStringSchema,
  phoneNumber: z.string().nullable(),
  role: OrganizationUserRolesSchema,
  title: z.string().nullable(),
  status: OrganizationUserStatusesSchema,
  permissions: z.array(z.nativeEnum(PERMISSION)),
  id: z.string().uuid(),
});

export type OrganizationUserType = z.infer<typeof OrganizationUserSchema>;

export const OrganizationPaginatedUserSchema = OrganizationUserSchema.pick({
  profilePicture: true,
  firstName: true,
  lastName: true,
  emailAddress: true,
  phoneNumber: true,
  title: true,
  status: true,
  permissions: true,
  id: true,
  createdAt: true,
}).extend({
  role: z.union([OrganizationUserRolesSchema, BrandMembershipRolesSchema]),
  isOrganizationUser: z.boolean().optional(),
  lastAccessedAt: z.string().nullable().optional(),
  associatedOrgIds: z.array(z.string()).optional(),
  associatedBrandIds: z.array(z.string()).optional(),
});

export type OrganizationPaginatedUserType = z.infer<typeof OrganizationPaginatedUserSchema>;

export const PaginatedOrganizationUsersResponseSchema = paginateResponseSchema(OrganizationPaginatedUserSchema);

export const PaginatedOrganizationUsersRequestQuerySchema = paginateQuerySchema({
  sortBy: ['firstName', 'lastName', 'role', 'status'] as const,
}).extend({ role: z.nativeEnum(OrganizationUserFilterQuery) });

export const CreateOrganizationUserRequestSchema = OrganizationUserSchema.pick({
  emailAddress: true,
  firstName: true,
  lastName: true,
  permissions: true,
});

export const CreateOrganizationUsersResponseSchema = OrganizationUserSchema;

export const UpdateOrganizationUsersRequestSchema = OrganizationUserSchema.pick({
  emailAddress: true,
  firstName: true,
  lastName: true,
  role: true,
  permissions: true,
}).partial();

export const UpdateOrganizationUsersResponseSchema = OrganizationUserSchema;

export const GetOrganizationUsersEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ organizationId }: { organizationId: string }) => `v1/organizations/${organizationId}/users`,
  responseSchema: PaginatedOrganizationUsersResponseSchema,
  requestSchema: PaginatedOrganizationUsersRequestQuerySchema,
});

export const AddOrganizationUserEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ organizationId }: { organizationId: string }) => `v1/organizations/${organizationId}/users`,
  responseSchema: z.void(),
  requestSchema: CreateOrganizationUserRequestSchema,
});

export const UpdateOrganizationUserEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ organizationId, userId }: { organizationId: string; userId: string }) =>
    `v1/organizations/${organizationId}/users/${userId}`,
  responseSchema: z.void(),
  requestSchema: UpdateOrganizationUsersRequestSchema,
});

export const DeleteOrganizationAdminEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ organizationId, userId }: { organizationId: string; userId: string }) =>
    `v1/organizations/${organizationId}/users/${userId}`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});

export const ResendOrganizationUserInviteEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ organizationId, userId }: { organizationId: string; userId: string }) =>
    `v1/organizations/${organizationId}/members/${userId}/resend-invitation`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});
